import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';

import { IInvestmentDataModel } from '../../../../../data-models/investment.data-model';
import { useSelectedCurrency } from '../../../../../hooks/useSelectedCurrency';
import { ViewModel } from '../../../../../models/view.model';
import { selectableFundsAtom } from '../../../../../services/state/AppConfigStateJ';
import { formulaFieldsListLoadable } from '../../../../../services/state/AdminPanel/FormulaFieldsStateJ';
import { applyFormulaFields, formulaFieldsForEntity } from '../../../../../util/formula-fields';
import { selectedViewTemporalDatePFJ } from '../../../state/PageStateJ';
import { roundTrackerMetricsByDateAtom } from '../../../state/RoundTrackerMetricsStateJ';
import { rowGroupColumnsState } from '../../../state/UIStateJ';
import { SharedTable } from './SharedTable';

export function RoundTrackerTable({ view }: { view: ViewModel }) {
  const date = useAtomValue(selectedViewTemporalDatePFJ);
  const { selectedCurrency } = useSelectedCurrency();
  const includeNonParticipatingRounds = view.includeNonParticipatingRounds ?? true;
  const allFunds = useAtomValue(selectableFundsAtom);
  const fundIds = useMemo(() => {
    const isAllFundsSelected = !view.filteredFunds;
    if (isAllFundsSelected && includeNonParticipatingRounds) {
      return undefined;
    }
    return isAllFundsSelected ? allFunds.map((f) => f.id) : view.filteredFunds!.map((f) => f.id);
  }, [view.filteredFunds, includeNonParticipatingRounds, allFunds]);
  const { data: metrics, isFetching } = useAtomValue(
    roundTrackerMetricsByDateAtom({
      date,
      currency: selectedCurrency,
      fundIds,
      includeNonParticipatingRounds,
    })
  );
  const setRowGroupColumns = useSetAtom(rowGroupColumnsState);

  // FD-2691: atomFamily creates a new atom per param set, so when fundIds changes the new
  // atom starts as undefined while the query is in flight. Passing undefined to AG Grid
  // clears all rows; when new data arrives, cells (including company logo images) remount
  // and briefly flash a broken-image state before they load. Hold onto the last loaded
  // metrics across refetches so rowData never drops to undefined mid-transition.
  const lastLoadedRef = useRef<{ metrics: IInvestmentDataModel[]; date: string } | undefined>(undefined);
  useEffect(() => {
    if (metrics) {
      lastLoadedRef.current = { metrics, date: date.toString() };
    }
  }, [metrics, date]);
  const baseRowData = metrics ?? lastLoadedRef.current?.metrics;
  // The date the rows on screen were fetched for, so held-over rows do not publish as the new date's.
  const rowsAsOfDate = metrics ? date.toString() : lastLoadedRef.current?.date;

  // Materialize round formula values on the investment rows so the grid
  // reads them as plain fields (row.formulaData.<key>). Grid-level on purpose:
  // the other consumers of roundTrackerMetricsByDateAtom (charts, simplified
  // grids) don't render formula columns. Loadable read: a failed formula fetch
  // must not take the round tracker down. No getCompany — round refs are all
  // row-local.
  const formulaLoad = useAtomValue(formulaFieldsListLoadable);
  const rowData = useMemo(() => {
    if (!baseRowData) {
      return baseRowData;
    }
    const formulaFields = formulaFieldsForEntity(
      formulaLoad.state === 'hasData' ? (formulaLoad.data ?? []) : [],
      'round'
    );
    // The rows were fetched as of the view's date; ASOFDATE() must agree.
    return applyFormulaFields(baseRowData, formulaFields, undefined, { asOfDate: date.toString() });
  }, [baseRowData, formulaLoad, date]);

  return (
    <SharedTable
      rowData={rowData}
      asOfDate={rowsAsOfDate}
      setRowGroupColumns={setRowGroupColumns}
      view={view}
      loading={isFetching}
    />
  );
}
